import { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { Camera, Image as ImageIcon, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import { useAuth } from '../../hooks/useAuth';
import { useWakeLock } from '../../hooks/useWakeLock';
import { apiClient } from '../../utils/api-client';
import { climbVideosService } from '../../services/climb-videos';
import { trackEvent } from '../../utils/analytics';
import type { ClimbVideo } from '../../types/climb-video';

interface UploadClimbVideoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  climbSlug: string;
  /** Called with the persisted video once the upload + save completes. */
  onUploaded: (video: ClimbVideo) => void;
}

// 'select' = chooser, 'uploading' = direct upload in flight, 'saving' = linking the blob to the climb.
type Phase = 'select' | 'uploading' | 'saving';

/**
 * Lets a logged-in user pick a video from the gallery or record one with the
 * camera and upload it straight to the climb (ActiveStorage direct upload →
 * createClimbVideo). Shows live upload progress and warns the user not to leave
 * the page until it finishes; closes itself on success.
 */
export function UploadClimbVideoDialog({ open, onOpenChange, climbSlug, onUploaded }: UploadClimbVideoDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { token } = useAuth();
  const [phase, setPhase] = useState<Phase>('select');
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const busy = phase === 'uploading' || phase === 'saving';

  // Keep the screen awake while the upload is in flight so the device doesn't
  // sleep and interrupt it.
  useWakeLock(busy);

  // Reset to the chooser whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) setPhase('select');
  }, [open]);

  // While a video is in flight, warn before the tab is closed/reloaded.
  useEffect(() => {
    if (!busy) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [busy]);

  // Mirror the API origin resolution used by the other direct-upload flows.
  const getApiOrigin = useCallback(() => {
    const fallback = {
      host: window.location.hostname,
      port: window.location.port || undefined,
      protocol: window.location.protocol.replace(':', ''),
    };
    const baseURL = apiClient.getBaseURL();
    if (!baseURL) return fallback;
    try {
      const url = new URL(baseURL);
      return { host: url.hostname, port: url.port || undefined, protocol: url.protocol.replace(':', '') };
    } catch {
      return fallback;
    }
  }, []);

  // The direct upload finished: persist the blob as a ClimbVideo, then close.
  const handleUploadComplete = useCallback((signedIds: string[]) => {
    const signedId = signedIds[0];
    if (!signedId) {
      setPhase('select');
      return;
    }
    setPhase('saving');
    climbVideosService.createClimbVideo(climbSlug, signedId)
      .then((video) => {
        trackEvent('climb_video_uploaded', { climb_slug: climbSlug });
        // The blob is saved but still transcoding in the background; tell the user
        // it'll be watchable shortly rather than implying it's ready right now.
        toast({ title: t('climbVideos.uploadProcessing') });
        onUploaded(video);
        onOpenChange(false);
      })
      .catch(() => {
        toast({ title: t('climbVideos.uploadError'), variant: 'destructive' });
        setPhase('select');
      });
  }, [climbSlug, onUploaded, onOpenChange, t, toast]);

  if (!token) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <DialogContent
        className="sm:max-w-md"
        // Don't let the user dismiss the dialog mid-upload.
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{t('climbVideos.uploadTitle')}</DialogTitle>
          <DialogDescription>{t('climbVideos.uploadSubtitle')}</DialogDescription>
        </DialogHeader>

        <DirectUploadProvider
          origin={getApiOrigin()}
          headers={{ Authorization: `Bearer ${token}` }}
          onSuccess={handleUploadComplete}
          render={({ handleUpload, uploads }) => {
            const uploading = uploads.find((u) => u.state === 'uploading');
            const progress = phase === 'saving' ? 100 : uploading ? Math.round(uploading.progress) : 0;

            const startUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              e.target.value = ''; // allow re-selecting the same file
              if (!file) return;
              if (!file.type.startsWith('video/')) {
                toast({ title: t('climbVideos.invalidFileType'), variant: 'destructive' });
                return;
              }
              trackEvent('climb_video_selected', { climb_slug: climbSlug });
              setPhase('uploading');
              handleUpload([file]);
            };

            return (
              <>
                {/* Hidden inputs: gallery (any video) + camera (record a new clip). */}
                <input ref={galleryInputRef} type="file" accept="video/*" className="hidden" onChange={startUpload} />
                <input ref={cameraInputRef} type="file" accept="video/*" capture="environment" className="hidden" onChange={startUpload} />

                {phase === 'select' ? (
                  <div className="flex flex-col gap-3">
                    <Button variant="outline" size="lg" className="justify-start" onClick={() => galleryInputRef.current?.click()}>
                      <ImageIcon className="mr-2 h-5 w-5" />
                      {t('climbVideos.uploadFromGallery')}
                    </Button>
                    <Button variant="outline" size="lg" className="justify-start" onClick={() => cameraInputRef.current?.click()}>
                      <Camera className="mr-2 h-5 w-5" />
                      {t('climbVideos.recordVideo')}
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 py-2">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm font-medium">
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {phase === 'saving' ? t('climbVideos.saving') : t('climbVideos.uploading')}
                        </span>
                        <span className="tabular-nums text-muted-foreground">{progress}%</span>
                      </div>
                      <div
                        className="h-2 w-full overflow-hidden rounded-full bg-muted"
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div className="h-full rounded-full bg-primary transition-all duration-200" style={{ width: `${progress}%` }} />
                      </div>
                    </div>

                    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span className="text-sm">{t('climbVideos.uploadWarning')}</span>
                    </div>
                  </div>
                )}
              </>
            );
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
